import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Stepper from "../shared/Stepper";
import BillingRateBuilderStepOne from "./BillingRateBuilderStepOne";
import BillingRateBuilderStepTwo from "./BillingRateBuilderStepTwo";
import BillingRateBuilderStepThree from "./BillingRateBuilderStepThree";
import BillingRateBuilderStepFour from "./BillingRateBuilderStepFour";
import {
    applyEffectiveDateToRatePayloads,
    injectRenewalResolvedDefinitions,
    parseLockDate,
    removeComponentReferencesFromRates,
    resyncRatesForComponent,
} from "./BillingRateBuilderShared";
import { getUnconfirmedSubCategoryMatches, applyHrpReconciliationToPayloads, applyHrpKeepReconciliationDecision, buildComponentWrapperFromRenewalFee, buildPreserveLockDateComponentNames, confirmAllSuggestedHrpReconciliation, extractBillingRateAccountHccId, filterBillableRenewalFees, getUnconfirmedHrpReconciliation, getHrpReconciliationEffectiveAction, isHrpReconciliationFullyResolved, prepareHrpReconciliationContext, removeHrpReconciliationComponentPayload, refreshHrpReconciliationRows, shouldRemoveHrpReconciliationComponentOnSkip, updateHrpReconciliationRow } from "../../utils/billingRateUtils";
import { checkBillingRateComponentCollisions, fetchAccounts, fetchBenefitPlansByAccountKey, fetchBillingRateAndComponents, fetchBillingRateCategories, fetchBillingRateComponentsByAccountKey, fetchBillingRateSubCategories, fetchHrpSubmissionTarget } from "../../utils/apiUtils";
import useBillingRateDraft from "../../hooks/useBillingRateDraft";
import "./BillingRateBuilder.scss";

const extractEffectiveDateFromRatePayloads = (ratePayloads) => {
    for (const wrapper of ratePayloads ?? []) {
        for (const cat of wrapper?.billingRatePayload?.billingRate?.billingCategoryRates ?? []) {
            for (const def of cat?.billingRateDefinitions ?? []) {
                const date = parseLockDate(def?.componentReference?.lockDate);
                if (date) return date;
            }
        }
    }
    return null;
};

const BILLING_RATE_BUILDER_INSTRUCTIONS = [
    { step: 1, instructions: "Import Data" },
    { step: 2, instructions: "Components" },
    { step: 3, instructions: "Rates" },
    { step: 4, instructions: "Review & Submit" },
];

function hrpEnvironmentBadge(target) {
    const shortLabel = target?.shortLabel?.trim() || null;
    const label = target?.label?.trim() || null;
    if (!shortLabel && !label) return null;
    return {
        text: (shortLabel || label).toUpperCase(),
        modifier: shortLabel || "unknown",
        title: `HRP ${label || shortLabel}`,
    };
}

const BillingRateBuilderMultiStepForm = () => {
    const [activeStep, setActiveStep] = useState(1);
    const [billingRateComponentPayloads, setBillingRateComponentPayloads] = useState([]);
    const [billingRatePayloads, setBillingRatePayloads] = useState([]);
    const [fees, setFees] = useState([]);
    const [plans, setPlans] = useState([]);
    const [uploadContext, setUploadContext] = useState(null);
    const [subCategoryRows, setSubCategoryRows] = useState([]);
    const [billingCategoryRows, setBillingCategoryRows] = useState([]);
    const [componentCollisions, setComponentCollisions] = useState([]);
    const [rateCollisions, setRateCollisions] = useState([]);
    const [groupId, setGroupId] = useState(null);
    const [tableGroupId, setTableGroupId] = useState(null);
    const [hrpSnapshot, setHrpSnapshot] = useState(null);
    const [hrpReconciliation, setHrpReconciliation] = useState([]);
    const [hrpReconciliationSettled, setHrpReconciliationSettled] = useState(false);
    const [activeSubmissionRecord, setActiveSubmissionRecord] = useState(null);
    const [planType, setPlanType] = useState(null);
    const [effectiveDate, setEffectiveDate] = useState(null);
    const formRef = useRef(null);
    const markGroupSubmittedRef = useRef(null);
    const prevTableGroupIdRef = useRef(null);

    const { data: hrpSubmissionTarget } = useQuery({
        queryKey: ["hrp-submission-target"],
        queryFn: fetchHrpSubmissionTarget,
        staleTime: Infinity,
        retry: 1,
    });
    const environmentBadge = hrpEnvironmentBadge(hrpSubmissionTarget);

    const draftGroupId = tableGroupId
        ?? activeSubmissionRecord?.tableGroupId
        ?? activeSubmissionRecord?.sk
        ?? null;

    const getDraftMeta = useCallback(
        () => ({
            groupId: draftGroupId,
            planType: planType ?? null,
            clientName: activeSubmissionRecord?.clientName ?? null,
        }),
        [draftGroupId, planType, activeSubmissionRecord],
    );

    const getDraftData = useCallback(
        () => ({
            componentPayloads: billingRateComponentPayloads,
            ratePayloads: billingRatePayloads,
            fees,
            plans,
            uploadContext,
            componentCollisions,
            rateCollisions,
            planType,
            clientName: activeSubmissionRecord?.clientName ?? null,
            hrpSnapshot,
            hrpReconciliation,
            hrpReconciliationSettled,
            groupId,
        }),
        [
            billingRateComponentPayloads,
            billingRatePayloads,
            fees,
            plans,
            uploadContext,
            componentCollisions,
            rateCollisions,
            planType,
            activeSubmissionRecord,
            hrpSnapshot,
            hrpReconciliation,
            hrpReconciliationSettled,
            groupId,
        ],
    );

    const hydrateFromDraft = useCallback(
        async ({ meta, data }, { refreshHrp = false } = {}) => {
            const {
                componentPayloads,
                ratePayloads,
                fees,
                plans,
                uploadContext,
                componentCollisions,
                rateCollisions,
                planType: draftPlanType,
                clientName,
                hrpSnapshot: savedHrpSnapshot,
                hrpReconciliation: savedHrpReconciliation,
                hrpReconciliationSettled: savedHrpReconciliationSettled,
                groupId: savedGroupId,
            } = data;
            const payloads = componentPayloads ?? [];
            const rates = ratePayloads ?? [];
            const accountHccId = extractBillingRateAccountHccId({ ratePayloads: rates, componentPayloads: payloads });
            const effectivePlanType = draftPlanType ?? planType ?? 'aso';

            let hrpContext = null;
            const hasSavedHrpState = Array.isArray(savedHrpReconciliation) && savedHrpReconciliation.length > 0;
            if (refreshHrp || !hasSavedHrpState) {
                try {
                    hrpContext = await prepareHrpReconciliationContext({
                        componentPayloads: payloads,
                        fees: fees ?? [],
                        ratePayloads: rates,
                        planType: effectivePlanType,
                        accountHccId,
                        clientName: clientName ?? meta?.clientName ?? null,
                        fetchAccounts,
                        fetchBenefitPlansByAccountKey,
                        fetchBillingRateComponentsByAccountKey,
                        fetchBillingRateAndComponents,
                    });
                } catch { /* non-fatal */ }
            }

            setBillingRateComponentPayloads(payloads);
            setBillingRatePayloads(rates);
            setFees(fees ?? []);
            setPlans(plans ?? []);
            setUploadContext(uploadContext ?? null);
            setComponentCollisions(componentCollisions ?? []);
            setRateCollisions(rateCollisions ?? []);
            if (refreshHrp && hrpContext) {
                setHrpSnapshot(hrpContext.hrpSnapshot);
                setHrpReconciliation(hrpContext.hrpReconciliation);
                setHrpReconciliationSettled(false);
            } else {
                setHrpSnapshot(savedHrpSnapshot ?? hrpContext?.hrpSnapshot ?? null);
                const restoredHrpReconciliation = savedHrpReconciliation ?? hrpContext?.hrpReconciliation ?? [];
                setHrpReconciliation(restoredHrpReconciliation);
                setHrpReconciliationSettled(
                    savedHrpReconciliationSettled === true
                    || isHrpReconciliationFullyResolved(restoredHrpReconciliation),
                );
            }
            setGroupId(savedGroupId ?? hrpContext?.groupId ?? accountHccId ?? null);
            if (meta?.groupId) setTableGroupId(meta.groupId);
            const derived = extractEffectiveDateFromRatePayloads(rates);
            if (derived) setEffectiveDate(derived);
            if (draftPlanType) setPlanType(draftPlanType);
            if (clientName || meta?.groupId) setActiveSubmissionRecord((prev) => ({
                ...prev,
                ...(clientName ? { clientName } : {}),
                ...(meta?.groupId ? { pk: "billing-rate-submission", sk: meta.groupId, tableGroupId: meta.groupId } : {}),
            }));
            if (payloads.length > 0 || rates.length > 0) {
                setActiveStep(2);
            }
            Promise.all([
                fetchBillingRateCategories().catch(() => null),
                fetchBillingRateSubCategories().catch(() => null),
            ]).then(([categories, subCategories]) => {
                if (Array.isArray(categories)) setBillingCategoryRows(categories);
                if (Array.isArray(subCategories)) setSubCategoryRows(subCategories);
            });
        },
        [],
    );

    const { saveDraft, clearDraft, resumeDraft, loadAllDraftsMeta } = useBillingRateDraft();

    const preserveDraft = useCallback(() => {
        if (!draftGroupId) return;
        const saved = saveDraft(getDraftMeta(), getDraftData(), { immediate: true });
        if (!saved) {
            toast.warn("Draft could not be saved — browser storage may be full.", {
                autoClose: 6000,
                className: "billing-rate-builder__toast--error",
            });
        }
    }, [draftGroupId, saveDraft, getDraftMeta, getDraftData]);

    useEffect(() => {
        if (!draftGroupId) return;
        if (
            billingRateComponentPayloads.length === 0 &&
            billingRatePayloads.length === 0
        ) return;
        const isNewSubmission = prevTableGroupIdRef.current !== draftGroupId;
        prevTableGroupIdRef.current = draftGroupId;
        saveDraft(getDraftMeta(), getDraftData(), { immediate: isNewSubmission });
    }, [
        draftGroupId,
        billingRateComponentPayloads,
        billingRatePayloads,
        fees,
        plans,
        uploadContext,
        componentCollisions,
        rateCollisions,
        planType,
        activeSubmissionRecord,
        hrpSnapshot,
        hrpReconciliation,
        hrpReconciliationSettled,
        groupId,
        saveDraft,
        getDraftMeta,
        getDraftData,
    ]);

    const handleHrpReconciliationExtracted = useCallback((rows) => {
        setHrpReconciliation(rows ?? []);
        setHrpReconciliationSettled(false);
    }, []);

    const handleExport = () => {
        const payload = {
            version: "1.0",
            exportedAt: new Date().toISOString(),
            ...getDraftData(),
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const planTypePrefix = `BillingRate_${planType === "aso" ? "ASO" : "LF"}`;
        const clientName = activeSubmissionRecord?.clientName
            ? `_${activeSubmissionRecord.clientName.replace(/[^a-zA-Z0-9]+(.)/g, (_, c) => c.toUpperCase()).replace(/[^a-zA-Z0-9]/g, "")}`
            : "";
        const timestamp = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `${planTypePrefix}${clientName}_${timestamp}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        formRef.current?.scrollTo({ top: 0 });
    }, [activeStep]);

    const componentInputs = useMemo(
        () => billingRateComponentPayloads.map((p) => p?.billingRateComponentPayload),
        [billingRateComponentPayloads],
    );

    const rateInputs = useMemo(
        () => billingRatePayloads.map((p) => p?.billingRatePayload),
        [billingRatePayloads],
    );

    // Replaces collision rows tied to the given component names with the freshly
    // fetched rows so step two's warnings reflect the edited/added component.
    // A null `newRows` means no re-check ran (name unchanged), so leave as-is.
    const mergeComponentCollisions = (prev, namesToRemove, newRows) => {
        if (newRows == null) return prev;
        const removeSet = new Set(namesToRemove.filter(Boolean));
        const filtered = prev.filter((row) => !removeSet.has(row?.existingName));
        return [...filtered, ...newRows];
    };

    const componentNameOf = (wrapper) =>
        wrapper?.billingRateComponentPayload?.billingRateComponent?.componentName;

    const handleComponentSave = (index, oldComponentName, newWrapper, collisionRows) => {
        if (index == null) return;
        const oldWrapper = billingRateComponentPayloads[index];
        const wasRenewalRequired = oldWrapper?.renewalRequired === true;
        const isNowResolved = newWrapper?.renewalRequired === false;
        const nextComponents = billingRateComponentPayloads.slice();
        nextComponents[index] = newWrapper;
        let nextRates = resyncRatesForComponent(billingRatePayloads, oldComponentName, newWrapper, { fees });
        if (wasRenewalRequired && isNowResolved) {
            nextRates = injectRenewalResolvedDefinitions(nextRates, newWrapper, { fees, billingCategoryRows });
        }
        const nextCollisions = mergeComponentCollisions(
            componentCollisions,
            [oldComponentName, componentNameOf(newWrapper)],
            collisionRows,
        );
        setBillingRateComponentPayloads(nextComponents);
        setBillingRatePayloads(nextRates);
        setComponentCollisions(nextCollisions);

        if (draftGroupId) {
            const saved = saveDraft(
                { groupId: draftGroupId, planType, clientName: activeSubmissionRecord?.clientName ?? null },
                {
                    componentPayloads: nextComponents,
                    ratePayloads: nextRates,
                    fees,
                    plans,
                    uploadContext,
                    componentCollisions: nextCollisions,
                    rateCollisions,
                    planType,
                    clientName: activeSubmissionRecord?.clientName ?? null,
                    hrpSnapshot,
                    hrpReconciliation,
                    hrpReconciliationSettled,
                    groupId,
                },
                { immediate: true },
            );
            if (!saved) {
                toast.warn("Draft could not be saved — browser storage may be full.", {
                    autoClose: 6000,
                    className: "billing-rate-builder__toast--error",
                });
            }
        }
    };

    const handleComponentAdd = (newWrapper, collisionRows) => {
        if (!newWrapper) return;
        setBillingRateComponentPayloads((prev) => [...prev, newWrapper]);
        setComponentCollisions((prev) =>
            mergeComponentCollisions(prev, [componentNameOf(newWrapper)], collisionRows),
        );
    };

    const handleComponentDelete = (index, componentName) => {
        if (index == null) return;
        setBillingRateComponentPayloads((prev) => prev.filter((_, i) => i !== index));
        if (componentName) {
            setBillingRatePayloads((prev) =>
                removeComponentReferencesFromRates(prev, componentName),
            );
        }
    };

    const handleEffectiveDateChange = (date) => {
        setEffectiveDate(date);
        setBillingRatePayloads((prev) => applyEffectiveDateToRatePayloads(prev, date, {
            preserveLockDateComponents: buildPreserveLockDateComponentNames(
                billingRateComponentPayloads,
                hrpReconciliation,
            ),
        }));
    };

    const handleRateSave = (index, newPayload) => {
        if (index == null) return;
        setBillingRatePayloads((prev) => {
            const next = prev.slice();
            next[index] = newPayload;
            return next;
        });
    };

    const handleRateAdd = (newPayload) => {
        if (!newPayload) return;
        setBillingRatePayloads((prev) => [...prev, newPayload]);
    };

    const handleRateDelete = (index) => {
        if (index == null) return;
        setBillingRatePayloads((prev) => prev.filter((_, i) => i !== index));
    };

    // Confirms an approximate sub category match: the matched short name is
    // written onto the payload (kept to { codeSetName, shortName }) and the
    // confirmation flag is recorded on the wrapper's sidecar match metadata.
    const confirmSubCategoryMatch = (wrapper) => {
        const rsc =
            wrapper?.billingRateComponentPayload?.billingRateComponent?.definition
                ?.rateClassifications?.rateSubCategory;
        const match = wrapper?.subCategoryMatch;
        if (!rsc || !match) return wrapper;
        return {
            ...wrapper,
            subCategoryMatch: { ...match, confirmed: true },
            billingRateComponentPayload: {
                ...wrapper.billingRateComponentPayload,
                billingRateComponent: {
                    ...wrapper.billingRateComponentPayload.billingRateComponent,
                    definition: {
                        ...wrapper.billingRateComponentPayload.billingRateComponent.definition,
                        rateClassifications: {
                            ...wrapper.billingRateComponentPayload.billingRateComponent.definition
                                .rateClassifications,
                            rateSubCategory: {
                                codeSetName: rsc.codeSetName,
                                shortName: match.matchedShortName ?? rsc.shortName,
                            },
                        },
                    },
                },
            },
        };
    };

    const handleSubCategoryMatchConfirm = (index) => {
        setBillingRateComponentPayloads((prev) => {
            const wrapper = prev[index];
            if (!wrapper?.subCategoryMatch) return prev;
            const next = prev.slice();
            next[index] = confirmSubCategoryMatch(wrapper);
            return next;
        });
    };

    const handleSubCategoryMatchConfirmAll = () => {
        const unconfirmed = getUnconfirmedSubCategoryMatches(billingRateComponentPayloads);
        if (unconfirmed.length === 0) return;
        setBillingRateComponentPayloads((prev) => {
            const next = prev.slice();
            unconfirmed.forEach(({ index }) => {
                next[index] = confirmSubCategoryMatch(next[index]);
            });
            return next;
        });
    };

    // Keep HRP reconciliation rows in sync until the drawer is saved; after that,
    // component edits should not reopen reconciliation or block step 3.
    const hasHrpReconciliation = (hrpSnapshot?.components?.length ?? 0) > 0;
    useEffect(() => {
        if (!hasHrpReconciliation || hrpReconciliationSettled) return;
        setHrpReconciliation((prev) =>
            refreshHrpReconciliationRows({
                hrpSnapshot,
                componentPayloads: billingRateComponentPayloads,
                ratePayloads: billingRatePayloads,
                planType: planType ?? 'aso',
                accountHccId: groupId,
                renewalFees: filterBillableRenewalFees(fees),
                previousRows: prev,
                globalCollisionRows: componentCollisions,
            }),
        );
    }, [fees, billingRateComponentPayloads, billingRatePayloads, hrpSnapshot, groupId, planType, hasHrpReconciliation, hrpReconciliationSettled, componentCollisions]);

    const applyHrpReconciliationPayloadUpdates = useCallback((nextRows) => {
        const payloads = [...billingRateComponentPayloads];
        const nextRates = [...billingRatePayloads];
        applyHrpReconciliationToPayloads({
            componentPayloads: payloads,
            reconciliationRows: nextRows,
            groupId,
            globalCollisionRows: componentCollisions,
            ratePayloads: nextRates,
        });
        setBillingRateComponentPayloads(payloads);

        const confirmedExtracted = nextRows.filter(
            (row) => row.confirmed && !row.skipped && row.extractedIndex != null,
        );
        let ratesAfterInject = nextRates;
        if (confirmedExtracted.length > 0) {
            confirmedExtracted.forEach((row) => {
                ratesAfterInject = injectRenewalResolvedDefinitions(
                    ratesAfterInject,
                    payloads[row.extractedIndex],
                    { fees, billingCategoryRows },
                );
            });
        }
        setBillingRatePayloads(ratesAfterInject);
        return payloads;
    }, [billingRateComponentPayloads, billingRatePayloads, groupId, fees, billingCategoryRows, componentCollisions]);

    const onStepClick = (i) => {
        const nextStep = i + 1;
        if (activeStep === 2 && nextStep > 2) {
            const unconfirmedSubCat = getUnconfirmedSubCategoryMatches(billingRateComponentPayloads);
            if (unconfirmedSubCat.length > 0) return;
            const unconfirmedHrp = hrpReconciliationSettled
                ? []
                : getUnconfirmedHrpReconciliation(hrpReconciliation);
            if (unconfirmedHrp.length > 0) {
                toast.info("Save HRP links in the review panel before continuing.", {
                    autoClose: 5000,
                    className: "billing-rate-builder__toast--info",
                });
                return;
            }
        }
        setActiveStep(nextStep);
    };

    const handleHrpReconciliationCreateFromRenewal = useCallback(async (rowId, action = "create") => {
        const row = hrpReconciliation.find((r) => r.rowId === rowId);
        if (row?.renewalFeeIndex == null) return;

        const renewalFee = filterBillableRenewalFees(fees)[row.renewalFeeIndex];
        if (!renewalFee) return;

        const gId = billingRatePayloads[0]?.account ?? groupId;
        const newWrapper = buildComponentWrapperFromRenewalFee(renewalFee, {
            groupId: gId,
            plans: billingRatePayloads.map((r) => r.plan).filter(Boolean),
            hrpComponentName: row.hrpComponentName ?? null,
        });

        let collisionRows = [];
        try {
            const componentName = newWrapper.billingRateComponentPayload.billingRateComponent.componentName;
            const res = await checkBillingRateComponentCollisions([
                { componentName, componentId: componentName },
            ]).catch(() => null);
            collisionRows = Array.isArray(res?.rows) ? res.rows : [];
        } catch { /* non-fatal */ }

        const newIndex = billingRateComponentPayloads.length;
        const payloads = [...billingRateComponentPayloads, newWrapper];
        const nextRows = updateHrpReconciliationRow(hrpReconciliation, rowId, {
            confirmed: true,
            skipped: false,
            userAction: action,
            extractedIndex: newIndex,
        });

        applyHrpReconciliationToPayloads({
            componentPayloads: payloads,
            reconciliationRows: nextRows,
            groupId,
            globalCollisionRows: componentCollisions,
        });

        setBillingRateComponentPayloads(payloads);
        setHrpReconciliation(nextRows);
        setComponentCollisions((prev) =>
            mergeComponentCollisions(prev, [componentNameOf(newWrapper)], collisionRows),
        );
        setBillingRatePayloads((prev) =>
            injectRenewalResolvedDefinitions(prev, payloads[newIndex], { fees, billingCategoryRows }),
        );
    }, [
        hrpReconciliation,
        fees,
        billingRatePayloads,
        groupId,
        billingRateComponentPayloads,
        billingCategoryRows,
        mergeComponentCollisions,
        componentNameOf,
    ]);

    const handleHrpReconciliationConfirm = useCallback(async (rowId, action) => {
        const row = hrpReconciliation.find((r) => r.rowId === rowId);
        if (row?.matchType === "hrp-only+renewal" && row.extractedIndex == null) {
            await handleHrpReconciliationCreateFromRenewal(rowId, action ?? "amend");
            return;
        }
        setHrpReconciliation((prev) => {
            const next = updateHrpReconciliationRow(prev, rowId, {
                confirmed: true,
                skipped: false,
                userAction: action,
            });
            applyHrpReconciliationPayloadUpdates(next);
            return next;
        });
    }, [applyHrpReconciliationPayloadUpdates, handleHrpReconciliationCreateFromRenewal, hrpReconciliation]);

    const handleHrpReconciliationSkip = useCallback((rowId) => {
        setHrpReconciliation((prev) =>
            updateHrpReconciliationRow(prev, rowId, {
                confirmed: true,
                skipped: true,
                userAction: "skip",
            }),
        );
    }, []);

    const handleHrpReconciliationKeep = useCallback((rowId) => {
        const row = hrpReconciliation.find((r) => r.rowId === rowId);
        if (!row) return;

        const payloads = [...billingRateComponentPayloads];
        const keepPatch = applyHrpKeepReconciliationDecision({
            componentPayloads: payloads,
            row,
            groupId,
            plans: billingRatePayloads.map((r) => r.plan).filter(Boolean),
            hrpSnapshot,
        });

        const nextRows = updateHrpReconciliationRow(hrpReconciliation, rowId, {
            confirmed: true,
            skipped: false,
            userAction: "keep",
            ...keepPatch,
        });

        setBillingRateComponentPayloads(payloads);
        setHrpReconciliation(nextRows);

        if (keepPatch.extractedIndex != null && payloads[keepPatch.extractedIndex]) {
            setBillingRatePayloads((prev) =>
                injectRenewalResolvedDefinitions(
                    prev,
                    payloads[keepPatch.extractedIndex],
                    { fees, billingCategoryRows },
                ),
            );
        }
    }, [hrpReconciliation, billingRateComponentPayloads, groupId, fees, billingCategoryRows, billingRatePayloads, hrpSnapshot]);

    const handleHrpReconciliationApplyAll = useCallback(async (draftActions) => {
        let payloads = [...billingRateComponentPayloads];
        let rows = [...hrpReconciliation];
        let nextCollisions = componentCollisions;
        const removedComponentNames = [];
        const appliedRowIds = new Set();

        const gId = billingRatePayloads[0]?.account ?? groupId;
        const planList = billingRatePayloads.map((r) => r.plan).filter(Boolean);

        for (const row of hrpReconciliation) {
            const action = draftActions[row.rowId] ?? getHrpReconciliationEffectiveAction(row);
            const previous = getHrpReconciliationEffectiveAction(row);
            if (row.confirmed && action === previous) continue;

            let currentRow = rows.find((r) => r.rowId === row.rowId) ?? row;

            const savedAction = row.confirmed ? (row.userAction ?? row.suggestedAction) : null;
            const shouldRemovePayload = shouldRemoveHrpReconciliationComponentOnSkip(currentRow, {
                action,
                previousAction: savedAction ?? previous,
            });

            if (shouldRemovePayload) {
                const removed = removeHrpReconciliationComponentPayload(
                    payloads,
                    rows,
                    currentRow.extractedIndex,
                );
                payloads = removed.payloads;
                rows = removed.rows;
                if (removed.removedName) removedComponentNames.push(removed.removedName);
                currentRow = rows.find((r) =>
                    r.rowId === row.rowId
                    || (row.renewalFeeIndex != null && r.renewalFeeIndex === row.renewalFeeIndex)
                    || (row.hrpComponentKey && r.hrpComponentKey === row.hrpComponentKey),
                ) ?? { ...currentRow, extractedIndex: null, confirmed: false, skipped: false, userAction: null };
            }

            if (action === "skip") {
                rows = updateHrpReconciliationRow(rows, currentRow.rowId, {
                    confirmed: true,
                    skipped: true,
                    userAction: "skip",
                    hrpOnlyKeepCreated: false,
                });
                appliedRowIds.add(currentRow.rowId);
                continue;
            }

            if (action === "keep") {
                const keepPatch = applyHrpKeepReconciliationDecision({
                    componentPayloads: payloads,
                    row: currentRow,
                    groupId: gId,
                    plans: planList,
                    hrpSnapshot,
                });
                rows = updateHrpReconciliationRow(rows, currentRow.rowId, {
                    confirmed: true,
                    skipped: false,
                    userAction: "keep",
                    hrpOnlyKeepCreated: keepPatch.hrpOnlyKeepCreated ?? false,
                    ...keepPatch,
                });
                appliedRowIds.add(keepPatch.rowId ?? currentRow.rowId);
                continue;
            }

            const needsCreate = action === "create"
                || (action === "amend" && currentRow.matchType === "hrp-only+renewal" && currentRow.extractedIndex == null);

            if (needsCreate && currentRow.renewalFeeIndex != null && !(row.confirmed && previous === "create")) {
                const renewalFee = filterBillableRenewalFees(fees)[currentRow.renewalFeeIndex];
                if (!renewalFee) continue;

                const newWrapper = buildComponentWrapperFromRenewalFee(renewalFee, {
                    groupId: gId,
                    plans: planList,
                    hrpComponentName: currentRow.hrpComponentName ?? null,
                });

                let collisionRows = [];
                try {
                    const componentName = newWrapper.billingRateComponentPayload.billingRateComponent.componentName;
                    const res = await checkBillingRateComponentCollisions([
                        { componentName, componentId: componentName },
                    ]).catch(() => null);
                    collisionRows = Array.isArray(res?.rows) ? res.rows : [];
                } catch { /* non-fatal */ }

                const newIndex = payloads.length;
                payloads = [...payloads, newWrapper];
                rows = updateHrpReconciliationRow(rows, currentRow.rowId, {
                    confirmed: true,
                    skipped: false,
                    userAction: action === "amend" ? "amend" : "create",
                    extractedIndex: newIndex,
                    rowId: `extracted-${newIndex}`,
                    hrpOnlyKeepCreated: false,
                    drawerSectionKey: currentRow.drawerSectionKey ?? "new-from-upload",
                });
                nextCollisions = mergeComponentCollisions(
                    nextCollisions,
                    [componentNameOf(newWrapper)],
                    collisionRows,
                );
                appliedRowIds.add(`extracted-${newIndex}`);
                continue;
            }

            if (action === "amend" || action === "create") {
                rows = updateHrpReconciliationRow(rows, currentRow.rowId, {
                    confirmed: true,
                    skipped: false,
                    userAction: action,
                    hrpOnlyKeepCreated: false,
                });
                appliedRowIds.add(currentRow.rowId);
            }
        }

        let nextRates = [...billingRatePayloads];
        if (removedComponentNames.length > 0) {
            nextRates = removedComponentNames.reduce(
                (next, name) => removeComponentReferencesFromRates(next, name),
                nextRates,
            );
        }

        applyHrpReconciliationToPayloads({
            componentPayloads: payloads,
            reconciliationRows: rows,
            groupId,
            globalCollisionRows: nextCollisions,
            ratePayloads: nextRates,
        });

        const appliedRows = rows.filter(
            (row) => appliedRowIds.has(row.rowId) && row.confirmed && !row.skipped && row.extractedIndex != null,
        );

        if (appliedRows.length > 0) {
            appliedRows.forEach((row) => {
                if (payloads[row.extractedIndex]) {
                    nextRates = injectRenewalResolvedDefinitions(
                        nextRates,
                        payloads[row.extractedIndex],
                        { fees, billingCategoryRows },
                    );
                }
            });
        }

        setBillingRateComponentPayloads(payloads);
        setComponentCollisions(nextCollisions);
        setHrpReconciliation(rows);
        setHrpReconciliationSettled(true);
        setBillingRatePayloads(nextRates);

        if (draftGroupId) {
            const saved = saveDraft(
                { groupId: draftGroupId, planType, clientName: activeSubmissionRecord?.clientName ?? null },
                {
                    componentPayloads: payloads,
                    ratePayloads: nextRates,
                    fees,
                    plans,
                    uploadContext,
                    componentCollisions: nextCollisions,
                    rateCollisions,
                    planType,
                    clientName: activeSubmissionRecord?.clientName ?? null,
                    hrpSnapshot,
                    hrpReconciliation: rows,
                    hrpReconciliationSettled: true,
                    groupId,
                },
                { immediate: true },
            );
            if (!saved) {
                toast.warn("Draft could not be saved — browser storage may be full.", {
                    autoClose: 6000,
                    className: "billing-rate-builder__toast--error",
                });
            }
        }
    }, [
        billingRateComponentPayloads,
        hrpReconciliation,
        componentCollisions,
        billingRatePayloads,
        groupId,
        fees,
        billingCategoryRows,
        mergeComponentCollisions,
        componentNameOf,
        hrpSnapshot,
        draftGroupId,
        planType,
        activeSubmissionRecord,
        plans,
        uploadContext,
        rateCollisions,
        saveDraft,
    ]);

    const handleHrpReconciliationConfirmAll = useCallback(() => {
        setHrpReconciliation((prev) => {
            const next = confirmAllSuggestedHrpReconciliation(prev);
            applyHrpReconciliationPayloadUpdates(next);
            setHrpReconciliationSettled(isHrpReconciliationFullyResolved(next));
            return next;
        });
    }, [applyHrpReconciliationPayloadUpdates]);

    const hasPayloads =
        billingRateComponentPayloads.length > 0 || billingRatePayloads.length > 0;

    return (
        <div className="billing-rate-builder container">
            <div className="billing-rate-builder__header">
                <div className="billing-rate-builder__header-leading">
                    <div className="title">Billing Rate Builder</div>
                    {environmentBadge && (
                        <span
                            className={`billing-rate-builder__env-badge billing-rate-builder__env-badge--${environmentBadge.modifier}`}
                            title={environmentBadge.title}
                        >
                            {environmentBadge.text}
                        </span>
                    )}
                </div>
                {activeStep >= 2 && hasPayloads && (
                    <button type="button" className="btn billing-rate-builder__reset-btn" onClick={handleExport} title="Download the current builder payload as a JSON file">
                        Export
                    </button>
                )}
            </div>

            {activeStep >= 2 && (
                <div className="billing-rate-builder__stepper-wrap steps-wrapper">
                    <Stepper
                        steps={BILLING_RATE_BUILDER_INSTRUCTIONS.map((ins) => ({ label: ins.instructions }))}
                        activeStep={activeStep}
                        onClickHandler={onStepClick}
                    />
                </div>
            )}

            <div ref={formRef} className="billing-rate-builder__form form scrollable-form">
                <BillingRateBuilderStepOne
                    activeStep={activeStep}
                    onComponentPayloadsExtracted={setBillingRateComponentPayloads}
                    onRatePayloadsExtracted={(payloads) => {
                        setBillingRatePayloads(payloads);
                        const derived = extractEffectiveDateFromRatePayloads(payloads);
                        if (derived) setEffectiveDate(derived);
                    }}
                    onFeesExtracted={setFees}
                    onPlansExtracted={setPlans}
                    onUploadContextExtracted={setUploadContext}
                    onSubCategoryRowsExtracted={setSubCategoryRows}
                    onBillingCategoryRowsExtracted={setBillingCategoryRows}
                    onComponentCollisionsExtracted={setComponentCollisions}
                    onRateCollisionsExtracted={setRateCollisions}
                    onHrpSnapshotExtracted={setHrpSnapshot}
                    onHrpReconciliationExtracted={handleHrpReconciliationExtracted}
                    onGroupIdExtracted={setGroupId}
                    onPlanTypeExtracted={setPlanType}
                    onProceed={(submissionRecord) => {
                        const nextTableGroupId = submissionRecord?.tableGroupId ?? submissionRecord?.sk ?? null;
                        if (nextTableGroupId) setTableGroupId(nextTableGroupId);
                        setActiveSubmissionRecord(submissionRecord);
                        setActiveStep(2);
                    }}
                    onImport={({ componentPayloads, ratePayloads, fees, plans, uploadContext, componentCollisions, rateCollisions, planType: importedPlanType, clientName: importedClientName, tableGroupId: importedTableGroupId, hrpReconciliationSettled: importedHrpReconciliationSettled }) => {
                        setBillingRateComponentPayloads(componentPayloads ?? []);
                        setBillingRatePayloads(ratePayloads ?? []);
                        setFees(fees ?? []);
                        setPlans(plans ?? []);
                        setUploadContext(uploadContext ?? null);
                        setComponentCollisions(componentCollisions ?? []);
                        setRateCollisions(rateCollisions ?? []);
                        setHrpReconciliationSettled(importedHrpReconciliationSettled === true);
                        const derived = extractEffectiveDateFromRatePayloads(ratePayloads ?? []);
                        if (derived) setEffectiveDate(derived);
                        if (importedPlanType) setPlanType(importedPlanType);
                        if (importedClientName) setActiveSubmissionRecord((prev) => ({ ...prev, clientName: importedClientName }));
                        if (importedTableGroupId) setTableGroupId(importedTableGroupId);
                    }}
                    onProceedToNext={() => setActiveStep(2)}
                    onRegisterMarkSubmitted={(fn) => { markGroupSubmittedRef.current = fn; }}
                    getDraftsMeta={loadAllDraftsMeta}
                    onResumeDraft={(groupId, options) => {
                        const entry = resumeDraft(groupId);
                        if (entry) hydrateFromDraft(entry, options);
                    }}

                />
                <BillingRateBuilderStepTwo
                    activeStep={activeStep}
                    componentPayloads={billingRateComponentPayloads}
                    fees={fees}
                    subCategoryRows={subCategoryRows}
                    componentCollisions={componentCollisions}
                    groupId={groupId}
                    clientName={activeSubmissionRecord?.clientName}
                    hrpSnapshot={hrpSnapshot}
                    hrpReconciliation={hrpReconciliation}
                    hrpReconciliationSettled={hrpReconciliationSettled}
                    planType={planType ?? "aso"}
                    onHrpReconciliationApplyAll={handleHrpReconciliationApplyAll}
                    onComponentSave={handleComponentSave}
                    onComponentAdd={handleComponentAdd}
                    onComponentDelete={handleComponentDelete}
                    onSubCategoryMatchConfirm={handleSubCategoryMatchConfirm}
                    onSubCategoryMatchConfirmAll={handleSubCategoryMatchConfirmAll}
                />
                <BillingRateBuilderStepThree
                    activeStep={activeStep}
                    ratePayloads={billingRatePayloads}
                    componentPayloads={billingRateComponentPayloads}
                    fees={fees}
                    uploadContext={uploadContext}
                    plans={plans}
                    rateCollisions={rateCollisions}
                    billingCategoryRows={billingCategoryRows}
                    effectiveDate={effectiveDate}
                    onEffectiveDateChange={handleEffectiveDateChange}
                    onRateSave={handleRateSave}
                    onRateAdd={handleRateAdd}
                    onRateDelete={handleRateDelete}
                />
                <BillingRateBuilderStepFour
                    activeStep={activeStep}
                    componentInputs={componentInputs}
                    rateInputs={rateInputs}
                    componentPayloads={billingRateComponentPayloads}
                    ratePayloads={billingRatePayloads}
                    fees={fees}
                    uploadContext={uploadContext}
                    billingCategoryRows={billingCategoryRows}
                    plans={plans}
                    componentCollisions={componentCollisions}
                    rateCollisions={rateCollisions}
                    submissionRecord={activeSubmissionRecord}
                    groupId={groupId}
                    onHrpSubmitted={({ hasIssues } = {}) => {
                        if (!hasIssues && tableGroupId) markGroupSubmittedRef.current?.(tableGroupId);
                        handleExport();
                    }}
                    onClearDraft={clearDraft}
                    onPreserveDraft={preserveDraft}
                    onBackToHome={() => setActiveStep(1)}
                />
            </div>
        </div>
    );
};

export default BillingRateBuilderMultiStepForm;
